import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiExError, apiReponseErrorIncludes } from '@shared';
import { initialState } from './initialState';

export interface PolicyApplicationState {
  applications: ClientPolicyApplication[];
  status: PolicyApplicationModelStatus;
  error: string | null;
  embeddedSigningData: EmbeddedSigningViewResponse | null;
  lastCreatedApplicationId: string | null;
  formIdCache: Record<string, string>; // Cache for API-fetched form IDs by key (stateCode-formType-fundingPolicyTypeId)
  pendingFormFetchCacheKey: string | null; // Temporarily stores the cache key for the current form fetch
  formConfigCache: Record<string, PolicyFormConfig>; // Cache for complete form configs (definition + metadata) by form ID
  pendingFormIdFetch: string | null; // Temporarily stores the form ID for the current form fetch
}

export interface DocusignEnvelopeState {
  clientPolicyApplicationId: string;
  docusignPolicyApplicationEnvelopeId: string;
  envelopeId: string;
  forClientId: string;
  messageId: string;
  policyApplicationFormId: string;
  status: string;
}

export interface DocusignSubmissionDataState {
  processedDocusignData: any;
  submissionData: any;
}

const policyApplicationModelSlice = createSlice({
  name: 'policyApplicationModel',
  initialState: initialState.policyApplicationModel,
  reducers: {
    fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdRequest(
      state,
      action: PayloadAction<{ clientId: string }>,
    ) {
      state.status = 'fetching';
      state.error = null;
    },
    fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdResponse(
      state,
      action: PayloadAction<ClientPolicyApplication[]>,
    ) {
      state.status = 'fetchingComplete';
      state.error = null;
      state.applications = action.payload || [];
    },
    fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdFailure(
      state,
      action: PayloadAction<ApiExError>,
    ) {
      state.status = 'error';
      state.error = action.payload?.message || 'Failed to fetch policy applications';
      state.applications = [];
    },
    fetchDocusignEnvelopeStatusBySqsMessageIdRequest(
      state,
      action: PayloadAction<{ sqsMessageId: string; queryParams?: Record<string, string> }>,
    ) {
      state.status = 'fetchingEnvelopeStatus';
      state.error = null;
    },
    fetchDocusignEnvelopeStatusBySqsMessageIdResponse(
      state,
      action: PayloadAction<DocusignEnvelopeState>,
    ) {
      state.status = 'fetchingEnvelopeStatusComplete';
      state.error = null;
      state.docusignEnvelopeStatus = action.payload.status;
    },
    fetchDocusignEnvelopeStatusBySqsMessageIdFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'error';
      state.error = action.payload.message;
    },

    fetchDocusignSubmissionDataBySqsMessageIdRequest(
      state,
      action: PayloadAction<{ sqsMessageId: string; queryParams?: Record<string, string> }>,
    ) {
      state.status = 'fetchingEnvelopeSubmissionDataComparisonStatus';
      state.error = null;
    },
    fetchDocusignSubmissionDataBySqsMessageIdResponse(
      state,
      action: PayloadAction<DocusignSubmissionDataState>,
    ) {
      state.status = 'fetchingEnvelopeSubmissionDataComparisonStatusComplete';
      state.error = null;
      state.docusignSubmissionDataComparison = action.payload;
    },
    fetchDocusignSubmissionDataBySqsMessageIdFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'error';
      state.error = action.payload.message;
    },

    putPolicyApplicationForClientByClientIdRequest(
      state,
      action: PayloadAction<PutPolicyApplicationForClientBodyProps>,
    ) {
      state.status = 'creatingPolicyApplication';
      state.error = null;
    },

    putPolicyApplicationForClientByClientIdResponse(
      state,
      action: PayloadAction<{
        createdApplicationId: string;
        applications: ClientPolicyApplication[];
      }>,
    ) {
      state.status = 'creatingPolicyApplicationComplete';
      state.error = null;
      state.applications = action.payload.applications || [];
      state.lastCreatedApplicationId = action.payload.createdApplicationId || null;
    },

    putPolicyApplicationForClientByClientIdFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'creatingPolicyApplicationError';
      state.error = action.payload?.message || 'Failed to create policy application';
    },

    postPolicyApplicationFormSubmissionRequest(
      state,
      action: PayloadAction<PostPolicyApplicationFormSubmissionPayload>,
    ) {
      state.status = 'saving';
      state.error = null;
    },
    postPolicyApplicationFormSubmissionResponse(
      state,
      action: PayloadAction<ClientPolicyApplication[]>,
    ) {
      state.status = 'savingComplete';
      state.error = null;
      state.applications = action.payload || [];
    },
    postPolicyApplicationFormSubmissionFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'error';
      state.error = action.payload?.message || 'Failed to save submission';
    },

    // Clear error
    clearUnderwritingError(state) {
      state.error = null;
    },
    resetPolicyApplicationStateStatus(state) {
      state.status = null;
      state.lastCreatedApplicationId = null;
      state.docusignEnvelopeStatus = null;
    },
    resetUnderwritingState() {
      return initialState.policyApplicationModel;
    },
    // Unified DocuSign envelope actions
    postDocusignEnvelopeForClientByFormIdRequest(
      state,
      action: PayloadAction<DocusignEnvelopeRequest>,
    ) {
      const { mode } = action.payload;
      state.status = mode === 'envelope' ? 'postingEnvelope' : 'creatingEmbeddedView';
      state.error = null;
    },
    postDocusignEnvelopeForClientByFormIdResponse(
      state,
      action: PayloadAction<DocusignEnvelopeResponse>,
    ) {
      const { ...responseData } = action.payload;
      state.status =
        state.status === 'postingEnvelope' ? 'postingEnvelopeComplete' : 'embeddedViewReady';
      state.error = null;
      state.embeddedSigningData = responseData as EmbeddedSigningViewResponse;
    },
    postDocusignEnvelopeForClientByFormIdFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'postingEnvelopeError';
      state.error = `Failed to create DocuSign envelope. Please contact support if the problem persists.`;
    },

    updateSigningStatus(
      state,
      action: PayloadAction<
        'signing' | 'signingComplete' | 'embeddedSigningError' | 'embeddedSigningComplete'
      >,
    ) {
      state.status = action.payload;
      state.error = null;
    },

    postPolicyApplicationEmailRequest(
      state,
      action: PayloadAction<PostPolicyApplicationEmailProps>,
    ) {
      state.status = 'sendingFormEmail';
      state.error = null;
    },
    postPolicyApplicationEmailResponse(state, action: PayloadAction<unknown>) {
      state.status = 'sendingFormEmailComplete';
      state.error = null;
    },
    postPolicyApplicationEmailFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'sendingFormEmailError';
      state.error = action.payload?.message || 'Failed to send policy application email';
    },

    postPolicyApplicationStepStatusUpdateRequest(
      state,
      action: PayloadAction<PostPolicyApplicationStepProps>,
    ) {
      state.status = 'saving';
      state.error = null;
    },
    postPolicyApplicationStepStatusUpdateResponse(
      state,
      action: PayloadAction<ClientPolicyApplication[]>,
    ) {
      state.status = 'savingComplete';
      state.error = null;
      state.applications = action.payload || [];
    },
    postPolicyApplicationStepStatusUpdateFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'error';
      state.error = action.payload?.message || 'Failed to save policy application step';
    },

    deletePolicyApplicationByClientIdRequest(
      state,
      action: PayloadAction<{
        clientId: string;
        clientPolicyApplicationId: string;
      }>,
    ) {
      state.status = 'deleting';
      state.error = null;
    },
    deletePolicyApplicationByClientIdResponse(
      state,
      action: PayloadAction<ClientPolicyApplication[]>,
    ) {
      state.status = 'deletingComplete';
      state.error = null;
      state.applications = action.payload || [];
    },
    deletePolicyApplicationByClientIdFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'error';
      state.error = action.payload?.message || 'Failed to delete policy application';
    },

    putClientPolicyApplicationStepRequest(
      state,
      action: PayloadAction<PutClientPolicyApplicationStepBodyProps>,
    ) {
      state.status = 'savingStepStatus';
      state.error = null;
    },
    putClientPolicyApplicationStepResponse(
      state,
      action: PayloadAction<ClientPolicyApplication[]>,
    ) {
      state.status = 'savingStepStatusComplete';
      state.error = null;
      state.applications = action.payload || [];
    },
    putClientPolicyApplicationStepFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'savingStepStatusError';
      if (apiReponseErrorIncludes(action.payload, 'Care packet not yet delivered')) {
        state.error =
          'Care packet email delivery is in progress. Please wait until the email is delivered';
      } else {
        state.error = action.payload?.message || 'Failed to update policy application step';
      }
    },
    postPolicyApplicationFormPrefillRequest(
      state,
      action: PayloadAction<PostPolicyApplicationFormPrefillPayload>,
    ) {
      state.status = 'savingPrefill';
      state.error = null;
    },
    postPolicyApplicationFormPrefillResponse(
      state,
      action: PayloadAction<ClientPolicyApplication[]>,
    ) {
      state.status = 'savingPrefillComplete';
      state.error = null;
      state.applications = action.payload || [];
    },
    postPolicyApplicationFormPrefillFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'savingPrefillError';
      state.error = action.payload?.message || 'Failed to save prefill';
    },
    fetchFormsRequest(
      state,
      action: PayloadAction<{
        queryParams: {
          stateCode: string;
          formType: string;
          fundingPolicyTypeId: number;
        };
        cacheKey: string;
      }>,
    ) {
      // Store the cache key to use when the response arrives
      state.pendingFormFetchCacheKey = action.payload.cacheKey;
    },
    fetchFormsResponse(state, action: PayloadAction<PolicyFormConfig>) {
      // Store both the form ID in the cache key mapping and the complete config by form ID
      if (state.pendingFormFetchCacheKey && action.payload?.formMetadata?.formId) {
        const formId = action.payload.formMetadata.formId;
        state.formIdCache[state.pendingFormFetchCacheKey] = formId;
        state.formConfigCache[formId] = action.payload;
        state.pendingFormFetchCacheKey = null;
      }
    },
    fetchFormsFailure(state, action: PayloadAction<ApiExError>) {
      // Silent failure, will fall back to static form IDs
      console.warn('Failed to fetch form ID from API:', action.payload?.message);
      state.pendingFormFetchCacheKey = null;
    },
    fetchFormByIdRequest(state, action: PayloadAction<{ id: string }>) {
      state.pendingFormIdFetch = action.payload.id;
    },
    fetchFormByIdResponse(state, action: PayloadAction<PolicyFormConfig | null>) {
      const formId = action.payload?.formMetadata?.formId || state.pendingFormIdFetch;
      if (formId && action.payload) {
        state.formConfigCache[formId] = action.payload;
      }
      state.pendingFormIdFetch = null;
    },
    fetchFormByIdFailure(state, action: PayloadAction<ApiExError>) {
      console.warn('Failed to fetch form by ID from API:', action.payload?.message);
      const formId = (action.payload?.metadata as any)?.id || state.pendingFormIdFetch;
      if (formId) {
        state.formConfigCache[formId] = null as any;
      }
      state.pendingFormIdFetch = null;
    },
    postPolicyApplicationFormNigoRequest(
      state,
      action: PayloadAction<PostPolicyApplicationFormNigoPayload>,
    ) {
      state.status = 'savingNigo';
      state.error = null;
    },
    postPolicyApplicationFormNigoResponse(
      state,
      action: PayloadAction<ClientPolicyApplication[]>,
    ) {
      state.status = 'savingNigoComplete';
      state.error = null;
      state.applications = action.payload || [];
    },
    postPolicyApplicationFormNigoFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'savingNigoError';
      state.error = action.payload?.message || 'Failed to save NIGO analysis';
    },
  },
});

export const {
  fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdRequest,
  fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdResponse,
  fetchPolicyApplicationsWithStatusUpdatesForClientByClientIdFailure,
  fetchDocusignEnvelopeStatusBySqsMessageIdRequest,
  fetchDocusignEnvelopeStatusBySqsMessageIdResponse,
  fetchDocusignEnvelopeStatusBySqsMessageIdFailure,
  fetchDocusignSubmissionDataBySqsMessageIdRequest,
  fetchDocusignSubmissionDataBySqsMessageIdResponse,
  fetchDocusignSubmissionDataBySqsMessageIdFailure,
  putPolicyApplicationForClientByClientIdRequest,
  putPolicyApplicationForClientByClientIdResponse,
  putPolicyApplicationForClientByClientIdFailure,
  postPolicyApplicationFormSubmissionRequest,
  postPolicyApplicationFormSubmissionResponse,
  postPolicyApplicationFormSubmissionFailure,
  clearUnderwritingError,
  resetUnderwritingState,
  resetPolicyApplicationStateStatus,
  postDocusignEnvelopeForClientByFormIdRequest,
  postDocusignEnvelopeForClientByFormIdResponse,
  postDocusignEnvelopeForClientByFormIdFailure,
  updateSigningStatus,
  postPolicyApplicationEmailRequest,
  postPolicyApplicationEmailResponse,
  postPolicyApplicationEmailFailure,
  postPolicyApplicationStepStatusUpdateRequest,
  postPolicyApplicationStepStatusUpdateResponse,
  postPolicyApplicationStepStatusUpdateFailure,
  deletePolicyApplicationByClientIdRequest,
  deletePolicyApplicationByClientIdResponse,
  deletePolicyApplicationByClientIdFailure,
  putClientPolicyApplicationStepRequest,
  putClientPolicyApplicationStepResponse,
  putClientPolicyApplicationStepFailure,
  postPolicyApplicationFormPrefillRequest,
  postPolicyApplicationFormPrefillResponse,
  postPolicyApplicationFormPrefillFailure,
  fetchFormsRequest,
  fetchFormsResponse,
  fetchFormsFailure,
  fetchFormByIdRequest,
  fetchFormByIdResponse,
  fetchFormByIdFailure,
  postPolicyApplicationFormNigoRequest,
  postPolicyApplicationFormNigoResponse,
  postPolicyApplicationFormNigoFailure,
} = policyApplicationModelSlice.actions;

export const underwritingModelReducer = policyApplicationModelSlice.reducer;
